import { BadgeIcon } from "./BadgeIcon";
import { Box, Typography } from "@mui/material";
import { useEffect } from "react";
import { BADGES, type BadgeId } from "@/utils/arcade/progress";
import type { ArcadeTheme } from "@/utils/arcade/theme";

const SHOW_MS = 5000;

// A small note at the top of the stage, where the score sits during play,
// when a run earns a badge.
export function BadgeToast({ badges, theme, onDone }: { badges: BadgeId[]; theme: ArcadeTheme; onDone: () => void }) {
  useEffect(() => {
    const timer = window.setTimeout(onDone, SHOW_MS);
    return () => window.clearTimeout(timer);
  }, [badges, onDone]);

  const first = BADGES[badges[0]];
  const more = badges.length - 1;
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "absolute",
        left: "50%",
        top: { xs: "10px", md: "14px" },
        transform: "translateX(-50%)",
        zIndex: 4,
        // Phones show the badge inside the game over card instead.
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        gap: "10px",
        maxWidth: "calc(100% - 132px)",
        p: "8px 16px 8px 8px",
        borderRadius: "999px",
        backgroundColor: "rgba(0,0,0,0.78)",
        backdropFilter: "blur(10px)",
        border: `1px solid color-mix(in srgb, ${theme.palette.accent} 55%, transparent)`,
        boxShadow: `0 12px 30px -12px ${theme.palette.primary}`,
        pointerEvents: "none",
        animation: "arcadeBadgeIn 0.35s cubic-bezier(0.23, 1, 0.32, 1)",
        "@keyframes arcadeBadgeIn": { from: { opacity: 0, transform: "translate(-50%, -10px)" }, to: { opacity: 1, transform: "translate(-50%, 0)" } },
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      <Box sx={{ width: 30, height: 30, borderRadius: "50%", display: "grid", placeItems: "center", flexShrink: 0, color: theme.palette.backgroundDeep, background: `linear-gradient(135deg, ${theme.palette.accent}, ${theme.palette.primary})` }}>
        <BadgeIcon id={badges[0]} size={19} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: "10px", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: theme.palette.accent, lineHeight: 1.2 }}>
          Badge earned
        </Typography>
        <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: "13px", fontWeight: 600, color: theme.palette.text, lineHeight: 1.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {first.name}: {first.detail}
          {more > 0 ? ` and ${more} more` : ""}
        </Typography>
      </Box>
    </Box>
  );
}

export default BadgeToast;
